import { useData } from "@hooks/useData";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { components } from "@apiSchema";
import { ID } from "@models";
import {
    CONSUMER_METRICS,
    ConsumerData,
    ConsumerDemographicCohort,
    ConsumerKeyword,
    ConsumerMetricData,
    DemographicGroup,
    KeywordRanking,
    Metric,
} from "@models/ConsumerData";

type Demographics = Record<DemographicGroup, ConsumerDemographicCohort[]>;

function emptyDemographics(): Demographics {
    return {
        [DemographicGroup.Gender]: [],
        [DemographicGroup.Generation]: [],
        [DemographicGroup.Ethnicity]: [],
        [DemographicGroup.Region]: [],
    };
}

const EMPTY_METRIC_DATA: ConsumerMetricData = {
    topKeywords: [],
    topKeywordsByChange: [],
    demographics: emptyDemographics(),
};

export const EMPTY_CATEGORY_PROFILE_CONSUMER: ConsumerData = {
    hasCpCategories: false,
    byMetric: CONSUMER_METRICS.reduce((acc, metric) => {
        acc[metric] = EMPTY_METRIC_DATA;
        return acc;
    }, {} as Record<Metric, ConsumerMetricData>),
};

const REQUEST_TYPE: keyof typeof apiConfig = "get category profile consumer";

type ConsumerDataModel = components["schemas"]["SnapCategoryProfileConsumerDataModel"];
type PerceptionResponse = components["schemas"]["SnapConsumerPerceptionResponse"];
type AffinityResponse = components["schemas"]["SnapAffinityIndexResponse"];
type KeywordTrend = components["schemas"]["SnapKeywordTrend"];
type SurveyInfo = { year: number; quarter: number };
type PerceptionEntry = Pick<ConsumerMetricData, "topKeywords" | "topKeywordsByChange">;

function toValidMetric(metric: number | null | undefined): Metric | null {
    const key = metric as Metric;
    return metric != null && CONSUMER_METRICS.includes(key) ? key : null;
}

function buildSurveyMap(
    surveys: components["schemas"]["SnapTrendSurvey"][] | null | undefined,
): Record<number, SurveyInfo> {
    const map: Record<number, SurveyInfo> = {};
    (surveys ?? []).forEach(({ surveyId, year, quarter }) => {
        if (surveyId != null) map[surveyId] = { year: year ?? 0, quarter: quarter ?? 0 };
    });
    return map;
}

function mapKeyword(keyword: KeywordTrend, surveyMap: Record<number, SurveyInfo>): ConsumerKeyword {
    const trend = (keyword.points ?? [])
        .filter(point => point.surveyId != null && point.value != null && surveyMap[point.surveyId])
        .map(point => ({
            ...surveyMap[point.surveyId as number],
            value: point.value as number,
        }));

    return {
        id: keyword.keywordId ?? 0,
        name: keyword.name ?? "",
        trend,
        twoYearChange: Math.round(keyword.twoYearChange ?? 0),
    };
}

function mapPerception(
    perception: PerceptionResponse | undefined,
): Partial<Record<Metric, PerceptionEntry>> {
    const surveyMap = buildSurveyMap(perception?.surveys);
    const result: Partial<Record<Metric, PerceptionEntry>> = {};

    (perception?.metrics ?? []).forEach(({ metric, groups }) => {
        const metricKey = toValidMetric(metric);
        if (metricKey === null) return;

        const entry: PerceptionEntry = { topKeywords: [], topKeywordsByChange: [] };
        (groups ?? []).forEach(group => {
            const keywords = (group.keywords ?? []).map(keyword => mapKeyword(keyword, surveyMap));
            if (group.ranking === KeywordRanking.ByChange) {
                entry.topKeywordsByChange = keywords;
            } else {
                entry.topKeywords = keywords;
            }
        });
        result[metricKey] = entry;
    });

    return result;
}

function mapAffinity(
    affinity: AffinityResponse | undefined,
): Partial<Record<Metric, Demographics>> {
    const result: Partial<Record<Metric, Demographics>> = {};

    (affinity?.metrics ?? []).forEach(({ metric, groups }) => {
        const metricKey = toValidMetric(metric);
        if (metricKey === null) return;

        const demographics = emptyDemographics();
        (groups ?? []).forEach(group => {
            const demoKey = group.demographic as DemographicGroup;
            if (group.demographic == null || !(demoKey in demographics)) return;
            demographics[demoKey] = (group.columns ?? []).map(column => ({
                id: column.respondentType ?? 0,
                name: column.name ?? "",
                items: (column.topKeywords ?? []).map(keyword => ({
                    name: keyword.name ?? "",
                    index: keyword.index ?? 0,
                })),
            }));
        });
        result[metricKey] = demographics;
    });

    return result;
}

function mapConsumerData(response: ConsumerDataModel): ConsumerData {
    const perception = mapPerception(response.consumerPerception);
    const affinity = mapAffinity(response.affinityIndex);

    const byMetric = CONSUMER_METRICS.reduce((acc, metricKey) => {
        acc[metricKey] = {
            topKeywords: perception[metricKey]?.topKeywords ?? [],
            topKeywordsByChange: perception[metricKey]?.topKeywordsByChange ?? [],
            demographics: affinity[metricKey] ?? emptyDemographics(),
        };
        return acc;
    }, {} as Record<Metric, ConsumerMetricData>);

    const hasCpCategories = CONSUMER_METRICS.some(metric => byMetric[metric].topKeywords.length > 0);

    return { hasCpCategories, byMetric };
}

async function getCategoryProfileConsumer(id: ID): Promise<ConsumerData> {
    try {
        const response: ConsumerDataModel = await http.get(REQUEST_TYPE, { id });
        return mapConsumerData(response);
    } catch (error) {
        console.error("Fetch error:", REQUEST_TYPE, error);
        return EMPTY_CATEGORY_PROFILE_CONSUMER;
    }
}

export default function useCategoryProfileConsumer(id: ID) {
    return useData<ConsumerData>(
        EMPTY_CATEGORY_PROFILE_CONSUMER,
        getCategoryProfileConsumer,
        id,
    );
}
